import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Tabs, Table, Button, Input, Select, Tag, Space, Modal,
  Form, notification, Switch, Row, Col, Statistic, Upload, Divider,
  Descriptions, Badge,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  SearchOutlined, HomeOutlined, BookOutlined, BankOutlined,
  PhoneOutlined, UploadOutlined, TeamOutlined, CommentOutlined,
  CheckCircleOutlined, CloseCircleOutlined, EnvironmentOutlined,
  StarOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../config';

const { TabPane } = Tabs;
const { Option } = Select;
const { TextArea } = Input;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

const TYPES = [
  { key: 'samaj_bhawan', labelHi: 'समाज भवन',  color: '#7a0000', icon: <HomeOutlined /> },
  { key: 'chhatrawas',   labelHi: 'छात्रावास',   color: '#0891b2', icon: <BankOutlined /> },
  { key: 'gurukul',      labelHi: 'गुरुकुल',     color: '#15803d', icon: <BookOutlined /> },
];
const REGIONS = [
  { key: 'rajasthan',   label: 'Rajasthan' },
  { key: 'other_state', label: 'Other States (India)' },
  { key: 'abroad',      label: 'Abroad / Deshavar' },
];
const RAJASTHAN_DISTS = [
  'Ajmer','Alwar','Banswara','Baran','Barmer','Bharatpur','Bhilwara','Bikaner',
  'Bundi','Chittorgarh','Churu','Dausa','Dholpur','Dungarpur','Hanumangarh',
  'Jaipur','Jaisalmer','Jalore','Jhalawar','Jhunjhunu','Jodhpur','Karauli',
  'Kota','Nagaur','Pali','Pratapgarh','Rajsamand','Sawai Madhopur','Sikar',
  'Sirohi','Sri Ganganagar','Tonk','Udaipur',
];
const INDIAN_STATES = [
  'Rajasthan','Uttar Pradesh','Maharashtra','Gujarat','Madhya Pradesh','Punjab',
  'Haryana','Delhi','Himachal Pradesh','Uttarakhand','Bihar','Jharkhand',
  'West Bengal','Odisha','Chhattisgarh','Telangana','Andhra Pradesh','Karnataka',
  'Tamil Nadu','Kerala','Assam','Goa','Haridwar (Uttarakhand)','Other',
];
const ABROAD_COUNTRIES = [
  'USA','UK','Canada','Australia','New Zealand','South Africa','Kenya',
  'UAE','Kuwait','Qatar','Bahrain','Oman','Germany','Other',
];

// ── Property Form ─────────────────────────────────────────────────────────────
function PropertyForm({ form, photoUrl, setPhotoUrl, uploading, setUploading }) {
  const region  = Form.useWatch('region_category', form);
  const isRaj   = region === 'rajasthan';
  const isAbroad = region === 'abroad';
  const typeKey = Form.useWatch('property_type', form);

  const uploadPhoto = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('cover', file);
      const r  = await axios.post(`${baseUrl}/admin/music/upload-cover`, fd,
        { headers: { ...auth(), 'Content-Type': 'multipart/form-data' } });
      if (r.data.status) { setPhotoUrl(r.data.url); notification.success({ message: 'Photo uploaded ✅', placement: 'topRight' }); }
    } catch { notification.error({ message: 'Upload failed', placement: 'topRight' }); }
    finally  { setUploading(false); }
    return false;
  };

  return (
    <Form form={form} layout="vertical" size="small">
      <Row gutter={12}>
        <Col span={8}>
          <Form.Item name="property_type" label="Type *" rules={[{ required: true }]}>
            <Select>{TYPES.map(t => <Option key={t.key} value={t.key}>{t.labelHi}</Option>)}</Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="region_category" label="Region *" rules={[{ required: true }]}>
            <Select>{REGIONS.map(r => <Option key={r.key} value={r.key}>{r.label}</Option>)}</Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="name" label="Official Name (optional)">
            <Input placeholder="e.g. श्री राजपुरोहित समाज भवन" />
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left" style={{ fontSize: 12, color: '#6b7280' }}>पूरा पता / Full Address</Divider>
      <Form.Item name="address_line1" label="Address Line 1 — मुख्य पता *" rules={[{ required: true }]}>
        <TextInput placeholder="Street / Colony / Road / मोहल्ला" />
      </Form.Item>
      <Form.Item name="address_line2" label="Address Line 2 — लैंडमार्क / नज़दीकी">
        <Input placeholder="Near _________ / _________ के पास" />
      </Form.Item>
      <Row gutter={12}>
        <Col span={8}>
          <Form.Item name="city" label="City / Town *" rules={[{ required: true }]}>
            <Input placeholder="e.g. Jodhpur / जोधपुर" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="tehsil" label="Tehsil">
            <Input placeholder="e.g. Sanchore" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="pincode" label="PIN Code">
            <Input placeholder="e.g. 342001" maxLength={6} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={8}>
          <Form.Item name="district" label="District / जिला">
            {isRaj
              ? <Select showSearch allowClear placeholder="Select district">
                  {RAJASTHAN_DISTS.map(d => <Option key={d} value={d}>{d}</Option>)}
                </Select>
              : <Input placeholder="e.g. Hyderabad" />
            }
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="state" label="State / Province">
            {isAbroad
              ? <Input placeholder="e.g. California, Ontario" />
              : <Select showSearch allowClear placeholder="Select state">
                  {INDIAN_STATES.map(s => <Option key={s} value={s}>{s}</Option>)}
                </Select>
            }
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="country" label="Country">
            {isAbroad
              ? <Select showSearch allowClear>
                  {ABROAD_COUNTRIES.map(c => <Option key={c} value={c}>{c}</Option>)}
                </Select>
              : <Input defaultValue="India" />
            }
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left" style={{ fontSize: 12, color: '#6b7280' }}>GPS & Map</Divider>
      <Row gutter={12}>
        <Col span={8}>
          <Form.Item name="latitude" label="Latitude (GPS)">
            <Input placeholder="e.g. 26.2957" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="longitude" label="Longitude (GPS)">
            <Input placeholder="e.g. 73.0169" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="map_link" label="Google Maps Link">
            <Input placeholder="https://maps.google.com/..." />
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left" style={{ fontSize: 12, color: '#6b7280' }}>संपर्क / Contact</Divider>
      <Row gutter={12}>
        <Col span={8}>
          <Form.Item name="contact_phone" label="Phone 1">
            <Input prefix={<PhoneOutlined />} placeholder="Primary contact" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="contact_phone2" label="Phone 2">
            <Input prefix={<PhoneOutlined />} placeholder="Secondary / alternate" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="contact_name" label="In-Charge / संपर्क व्यक्ति">
            <Input placeholder="Caretaker name" />
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left" style={{ fontSize: 12, color: '#6b7280' }}>अन्य विवरण</Divider>
      <Row gutter={12}>
        <Col span={8}>
          <Form.Item name="established_year" label="Est. Year / स्थापना वर्ष">
            <Input placeholder="e.g. 2016" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="capacity" label="Capacity / क्षमता">
            <Input placeholder="e.g. 200 persons / 50 students" />
          </Form.Item>
        </Col>
        {typeKey === 'gurukul' && (
          <Col span={8}>
            <Form.Item name="is_residential" label="Fully Residential?" valuePropName="checked">
              <Switch checkedChildren="हाँ (पूर्ण आवासीय)" unCheckedChildren="नहीं" />
            </Form.Item>
          </Col>
        )}
      </Row>
      <Form.Item name="facilities" label="Facilities / सुविधाएं (comma-separated)">
        <Input placeholder="e.g. Hall, Kitchen, Dining Room, Rooms, Parking, Library" />
      </Form.Item>
      <Form.Item name="description" label="Description / विवरण">
        <TextArea rows={2} />
      </Form.Item>
      <Form.Item label="Photo">
        {photoUrl && <img src={photoUrl} alt="" style={{ width: 100, height: 75, borderRadius: 8, objectFit: 'cover', marginBottom: 8, display: 'block' }} />}
        <Upload beforeUpload={uploadPhoto} showUploadList={false} accept="image/*">
          <Button icon={<UploadOutlined />} loading={uploading} size="small">{photoUrl ? 'Change Photo' : 'Upload Photo'}</Button>
        </Upload>
        {photoUrl && <Button size="small" danger style={{ marginLeft: 8 }} onClick={() => setPhotoUrl('')}>Remove</Button>}
      </Form.Item>
      <Row gutter={12}>
        <Col span={6}><Form.Item name="sort_order" label="Sort Order"><Input type="number" min={0} /></Form.Item></Col>
        <Col span={6}><Form.Item name="is_active" label="Active" valuePropName="checked"><Switch /></Form.Item></Col>
        <Col span={6}><Form.Item name="is_verified" label="Verified" valuePropName="checked"><Switch /></Form.Item></Col>
      </Row>
      <Form.Item name="admin_note" label="Admin Note (internal)"><Input /></Form.Item>
    </Form>
  );
}

// helper used in PropertyForm
function TextInput(props) { return <Input {...props} />; }

// ── Samiti Form ───────────────────────────────────────────────────────────────
function SamitiForm({ form }) {
  return (
    <Form form={form} layout="vertical" size="small">
      <Form.Item name="samiti_name" label="Samiti Name (Hindi) *" rules={[{ required: true }]}>
        <Input placeholder="e.g. श्री राजपुरोहित समाज सेवा समिति, जोधपुर" />
      </Form.Item>
      <Form.Item name="samiti_name_en" label="Samiti Name (English)">
        <Input placeholder="e.g. Shri Rajpurohit Samaj Seva Samiti, Jodhpur" />
      </Form.Item>
      <Divider orientation="left" style={{ fontSize: 11 }}>Key Office Bearers</Divider>
      <Row gutter={12}>
        <Col span={12}><Form.Item name="president_name"    label="President / अध्यक्ष"><Input /></Form.Item></Col>
        <Col span={12}><Form.Item name="president_phone"   label="President Phone"><Input prefix={<PhoneOutlined />} /></Form.Item></Col>
        <Col span={12}><Form.Item name="vice_president_name"  label="Vice President / उपाध्यक्ष"><Input /></Form.Item></Col>
        <Col span={12}><Form.Item name="vice_president_phone" label="Vice President Phone"><Input prefix={<PhoneOutlined />} /></Form.Item></Col>
        <Col span={12}><Form.Item name="secretary_name"    label="Secretary / मंत्री"><Input /></Form.Item></Col>
        <Col span={12}><Form.Item name="secretary_phone"   label="Secretary Phone"><Input prefix={<PhoneOutlined />} /></Form.Item></Col>
        <Col span={12}><Form.Item name="joint_secretary_name"  label="Joint Secretary / सह-मंत्री"><Input /></Form.Item></Col>
        <Col span={12}><Form.Item name="joint_secretary_phone" label="Joint Secretary Phone"><Input prefix={<PhoneOutlined />} /></Form.Item></Col>
        <Col span={12}><Form.Item name="treasurer_name"    label="Treasurer / कोषाध्यक्ष"><Input /></Form.Item></Col>
        <Col span={12}><Form.Item name="treasurer_phone"   label="Treasurer Phone"><Input prefix={<PhoneOutlined />} /></Form.Item></Col>
      </Row>
      <Divider orientation="left" style={{ fontSize: 11 }}>Registration</Divider>
      <Row gutter={12}>
        <Col span={8}><Form.Item name="registration_no"   label="Reg. No."><Input /></Form.Item></Col>
        <Col span={8}><Form.Item name="registration_date" label="Reg. Date"><Input placeholder="YYYY-MM-DD" /></Form.Item></Col>
        <Col span={8}><Form.Item name="registration_type" label="Reg. Type"><Input placeholder="Societies Act / Trust / Section 8" /></Form.Item></Col>
      </Row>
      <Row gutter={12}>
        <Col span={8}><Form.Item name="office_phone" label="Office Phone"><Input prefix={<PhoneOutlined />} /></Form.Item></Col>
        <Col span={8}><Form.Item name="email"        label="Email"><Input /></Form.Item></Col>
        <Col span={8}><Form.Item name="website"      label="Website"><Input /></Form.Item></Col>
      </Row>
      <Form.Item name="description" label="Description"><TextArea rows={2} /></Form.Item>
      <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}><Switch /></Form.Item>
    </Form>
  );
}

// ── Properties Tab (per type) ─────────────────────────────────────────────────
function PropertiesTab({ typeKey }) {
  const [data,      setData]      = useState([]);
  const [total,     setTotal]     = useState(0);
  const [loading,   setLoading]   = useState(false);
  const [page,      setPage]      = useState(1);
  const [search,    setSearch]    = useState('');
  const [regionFil, setRegionFil] = useState('');
  const [modal,     setModal]     = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [photoUrl,  setPhotoUrl]  = useState('');
  const [uploading, setUploading] = useState(false);
  // Samiti sub-panel
  const [samitiFor, setSamitiFor] = useState(null);
  const [samitiData,setSamitiData]= useState([]);
  const [samitiModal,setSamitiModal]= useState(false);
  const [editingSamiti,setEditingSamiti]= useState(null);
  const [savingSamiti,setSavingSamiti]= useState(false);
  const [propForm] = Form.useForm();
  const [samForm]  = Form.useForm();

  const fetch = useCallback(async (p = 1, s = search, r = regionFil) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: 50, type: typeKey, ...(s ? { search: s } : {}), ...(r ? { region: r } : {}) });
      const res = await axios.get(`${baseUrl}/admin/samaj-properties?${params}`, { headers: auth() });
      if (res.data.status) { setData(res.data.result || []); setTotal(res.data.total || 0); }
    } catch { notification.error({ message: 'Load failed', placement: 'topRight' }); }
    finally { setLoading(false); }
  }, [search, regionFil, typeKey]);

  useEffect(() => { fetch(1); }, [typeKey]);

  const openAdd = () => {
    propForm.resetFields();
    propForm.setFieldsValue({ property_type: typeKey, region_category: 'rajasthan', is_active: true, country: 'India', sort_order: 0 });
    setEditing(null); setPhotoUrl(''); setModal(true);
  };
  const openEdit = (row) => {
    propForm.setFieldsValue({ ...row, is_active: !!row.is_active, is_verified: !!row.is_verified, is_residential: !!row.is_residential });
    setEditing(row); setPhotoUrl(row.photo_url || ''); setModal(true);
  };
  const handleSave = async () => {
    const values = await propForm.validateFields();
    setSaving(true);
    try {
      const payload = { ...values, photo_url: photoUrl || null };
      if (editing) await axios.patch(`${baseUrl}/admin/samaj-properties/${editing.id}`, payload, { headers: auth() });
      else         await axios.post (`${baseUrl}/admin/samaj-properties`,               payload, { headers: auth() });
      notification.success({ message: editing ? 'Updated ✅' : 'Added ✅', placement: 'topRight' });
      setModal(false); fetch(page);
    } catch (err) { notification.error({ message: err?.response?.data?.message || 'Error', placement: 'topRight' }); }
    finally { setSaving(false); }
  };
  const handleDelete = (id) => {
    Modal.confirm({ title: 'Delete?', okType: 'danger', okText: 'Delete',
      onOk: async () => { await axios.delete(`${baseUrl}/admin/samaj-properties/${id}`, { headers: auth() }); fetch(page); } });
  };

  // Samiti
  const openSamiti = async (row) => {
    setSamitiFor(row);
    const r = await axios.get(`${baseUrl}/admin/samaj-properties/${row.id}/samiti`, { headers: auth() });
    setSamitiData(r.data.result || []);
  };
  const openAddSamiti  = () => { samForm.resetFields(); samForm.setFieldsValue({ is_active: true }); setEditingSamiti(null); setSamitiModal(true); };
  const openEditSamiti = (s) => { samForm.setFieldsValue({ ...s, is_active: !!s.is_active }); setEditingSamiti(s); setSamitiModal(true); };
  const saveSamiti = async () => {
    const values = await samForm.validateFields();
    setSavingSamiti(true);
    try {
      if (editingSamiti) await axios.patch(`${baseUrl}/admin/samaj-properties/${samitiFor.id}/samiti/${editingSamiti.id}`, values, { headers: auth() });
      else               await axios.post (`${baseUrl}/admin/samaj-properties/${samitiFor.id}/samiti`,                    values, { headers: auth() });
      notification.success({ message: editingSamiti ? 'Samiti Updated ✅' : 'Samiti Added ✅', placement: 'topRight' });
      setSamitiModal(false);
      const r = await axios.get(`${baseUrl}/admin/samaj-properties/${samitiFor.id}/samiti`, { headers: auth() });
      setSamitiData(r.data.result || []);
    } catch (err) { notification.error({ message: err?.response?.data?.message || 'Error', placement: 'topRight' }); }
    finally { setSavingSamiti(false); }
  };
  const deleteSamiti = async (samId) => {
    await axios.delete(`${baseUrl}/admin/samaj-properties/${samitiFor.id}/samiti/${samId}`, { headers: auth() });
    const r = await axios.get(`${baseUrl}/admin/samaj-properties/${samitiFor.id}/samiti`, { headers: auth() });
    setSamitiData(r.data.result || []);
  };

  const R_COLOR = { rajasthan: 'blue', other_state: 'orange', abroad: 'purple' };
  const R_LABEL = { rajasthan: 'Raj.', other_state: 'India', abroad: 'Abroad' };

  const columns = [
    { title: '#', dataIndex: 'id', width: 50 },
    {
      title: 'Location', render: (_, r) => (
        <div>
          {r.photo_url && <img src={r.photo_url} alt="" style={{ width: 40, height: 32, borderRadius: 5, objectFit: 'cover', marginRight: 8, verticalAlign: 'middle' }} />}
          <strong>{r.city}</strong>
          {r.name && <div style={{ fontSize: 11, color: '#7a0000' }}>{r.name}</div>}
          <div style={{ fontSize: 11, color: '#9ca3af' }}>{[r.district, r.state].filter(Boolean).join(', ')}</div>
        </div>
      ),
    },
    { title: 'Address', render: (_, r) => (
      <div style={{ fontSize: 12 }}>
        {r.address_line1}
        {r.address_line2 && <div style={{ color: '#9ca3af' }}>{r.address_line2}</div>}
        {r.pincode && <span style={{ color: '#6b7280' }}> — {r.pincode}</span>}
      </div>
    ), ellipsis: true },
    { title: 'Region', dataIndex: 'region_category', width: 75, render: v => <Tag color={R_COLOR[v]}>{R_LABEL[v]}</Tag> },
    { title: 'Phone', render: (_, r) => <div style={{ fontSize: 11 }}>{r.contact_phone}{r.contact_phone2 ? <div>{r.contact_phone2}</div> : null}</div>, width: 120 },
    { title: 'GPS', render: (_, r) => r.latitude ? <Tag color="green">✓</Tag> : <Tag color="red">—</Tag>, width: 50 },
    { title: 'Samiti', dataIndex: 'samiti_count', width: 60, render: (v, r) => (
      <Button size="small" type={v > 0 ? 'primary' : 'default'} onClick={() => openSamiti(r)}
        style={v > 0 ? { background: '#7a0000', borderColor: '#7a0000' } : {}}>
        {v || 0}
      </Button>
    )},
    { title: '✓', dataIndex: 'is_verified', width: 40, render: v => v ? <StarOutlined style={{ color: '#ca8a04' }} /> : '—' },
    {
      title: 'Actions', width: 90, render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(r.id)} />
        </Space>
      ),
    },
  ];

  const typeInfo = TYPES.find(t => t.key === typeKey);

  return (
    <div>
      <Row gutter={[10, 10]} style={{ marginBottom: 14 }}>
        <Col xs={24} sm={10} md={8}>
          <Input prefix={<SearchOutlined />} placeholder="Search city, district, address, name..."
            value={search} onChange={e => { setSearch(e.target.value); fetch(1, e.target.value, regionFil); }} allowClear />
        </Col>
        <Col xs={12} sm={6} md={4}>
          <Select style={{ width: '100%' }} placeholder="Region" value={regionFil || undefined}
            onChange={v => { setRegionFil(v || ''); fetch(1, search, v || ''); }} allowClear>
            {REGIONS.map(r => <Option key={r.key} value={r.key}>{r.label}</Option>)}
          </Select>
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}
            style={{ background: typeInfo?.color, borderColor: typeInfo?.color }}>
            Add {typeInfo?.labelHi}
          </Button>
        </Col>
        <Col><Button icon={<ReloadOutlined />} onClick={() => fetch(page)} /></Col>
      </Row>

      <Table dataSource={data} columns={columns} rowKey="id" loading={loading} size="small" scroll={{ x: 900 }}
        pagination={{ current: page, pageSize: 50, total, onChange: p => { setPage(p); fetch(p); }, showTotal: t => `Total ${t}` }} />

      {/* Property Add/Edit */}
      <Modal open={modal} title={editing ? `Edit: ${editing?.city}` : `Add New ${typeInfo?.labelHi}`}
        onCancel={() => setModal(false)} onOk={handleSave} confirmLoading={saving} width={820} okText={editing ? 'Update' : 'Add'}>
        <PropertyForm form={propForm} photoUrl={photoUrl} setPhotoUrl={setPhotoUrl} uploading={uploading} setUploading={setUploading} />
      </Modal>

      {/* Samiti Drawer */}
      <Modal open={!!samitiFor} title={<span><TeamOutlined /> समिति — {samitiFor?.city} {samitiFor?.name ? `(${samitiFor.name})` : ''}</span>}
        onCancel={() => setSamitiFor(null)} footer={null} width={700}>
        <Button type="primary" icon={<PlusOutlined />} size="small" onClick={openAddSamiti} style={{ marginBottom: 12, background: '#7a0000' }}>
          Add Samiti
        </Button>
        {samitiData.map(s => (
          <Card key={s.id} size="small" style={{ marginBottom: 10, borderLeft: '4px solid #7a0000' }}
            extra={
              <Space>
                <Button size="small" icon={<EditOutlined />} onClick={() => openEditSamiti(s)} />
                <Button size="small" danger icon={<DeleteOutlined />} onClick={() => deleteSamiti(s.id)} />
              </Space>
            }
            title={<span style={{ fontWeight: 700, color: '#7a0000' }}>{s.samiti_name}</span>}>
            <Descriptions size="small" column={2} bordered>
              {s.president_name   && <Descriptions.Item label="President">{s.president_name}  {s.president_phone   && <a href={`tel:${s.president_phone}`}   style={{ marginLeft: 6 }}>📞 {s.president_phone}</a>}</Descriptions.Item>}
              {s.secretary_name   && <Descriptions.Item label="Secretary">{s.secretary_name}  {s.secretary_phone   && <a href={`tel:${s.secretary_phone}`}   style={{ marginLeft: 6 }}>📞 {s.secretary_phone}</a>}</Descriptions.Item>}
              {s.treasurer_name   && <Descriptions.Item label="Treasurer">{s.treasurer_name}  {s.treasurer_phone   && <a href={`tel:${s.treasurer_phone}`}   style={{ marginLeft: 6 }}>📞 {s.treasurer_phone}</a>}</Descriptions.Item>}
              {s.registration_no  && <Descriptions.Item label="Reg. No.">{s.registration_no}</Descriptions.Item>}
              {s.office_phone     && <Descriptions.Item label="Office Phone">{s.office_phone}</Descriptions.Item>}
            </Descriptions>
          </Card>
        ))}
        {samitiData.length === 0 && <div style={{ textAlign: 'center', padding: 24, color: '#9ca3af' }}>No samiti linked yet. Click "Add Samiti" to add.</div>}
      </Modal>

      {/* Samiti Add/Edit */}
      <Modal open={samitiModal} title={editingSamiti ? 'Edit Samiti' : 'Add Samiti'}
        onCancel={() => setSamitiModal(false)} onOk={saveSamiti} confirmLoading={savingSamiti} width={720} okText={editingSamiti ? 'Update' : 'Add'}>
        <SamitiForm form={samForm} />
      </Modal>
    </div>
  );
}

// ── Comments Tab ──────────────────────────────────────────────────────────────
function CommentsTab() {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [status,  setStatus]  = useState('pending');
  const [page,    setPage]    = useState(1);
  const [total,   setTotal]   = useState(0);

  const fetch = useCallback(async (p = 1, s = status) => {
    setLoading(true);
    try {
      const r = await axios.get(`${baseUrl}/admin/samaj-properties/comments?status=${s}&page=${p}`, { headers: auth() });
      if (r.data.status) { setData(r.data.result || []); setTotal(r.data.total || 0); }
    } catch {} finally { setLoading(false); }
  }, [status]);

  useEffect(() => { fetch(1); }, []);

  const review = async (id, action) => {
    try {
      await axios.patch(`${baseUrl}/admin/samaj-properties/comments/${id}`, { action }, { headers: auth() });
      notification.success({ message: action === 'approve' ? 'Approved ✅' : 'Rejected', placement: 'topRight' });
      fetch(page);
    } catch (err) { notification.error({ message: err?.response?.data?.message || 'Error', placement: 'topRight' }); }
  };

  const columns = [
    { title: 'Property', render: (_, r) => <div><strong>{r.property_city}</strong><div style={{ fontSize: 11, color: '#6b7280' }}>{r.property_type?.replace('_',' ')}</div>{r.property_name && <div style={{ fontSize: 11, color: '#7a0000' }}>{r.property_name}</div>}</div>, width: 140 },
    { title: 'Commenter', render: (_, r) => <div><div style={{ fontWeight: 600 }}>{r.commenter_name}</div><div style={{ fontSize: 11, color: '#9ca3af' }}>{r.commenter_village}</div></div>, width: 140 },
    { title: 'Comment', dataIndex: 'comment_text', ellipsis: true },
    { title: 'GPS', render: (_, r) => r.contributed_lat ? <Tag color="green">📍 {parseFloat(r.contributed_lat).toFixed(4)}, {parseFloat(r.contributed_lng).toFixed(4)}</Tag> : '—', width: 140 },
    { title: 'Rating', dataIndex: 'rating', width: 60, render: v => v ? `⭐ ${v}` : '—' },
    { title: 'Date', dataIndex: 'created_at', width: 90, render: v => v?.slice(0, 10) },
    {
      title: 'Actions', width: 120, render: (_, r) => r.status === 'pending' ? (
        <Space>
          <Button size="small" type="primary" icon={<CheckCircleOutlined />} style={{ background: '#16a34a', borderColor: '#16a34a' }} onClick={() => review(r.id, 'approve')}>OK</Button>
          <Button size="small" danger icon={<CloseCircleOutlined />} onClick={() => review(r.id, 'reject')}>No</Button>
        </Space>
      ) : <Tag color={r.status === 'approved' ? 'green' : 'red'}>{r.status}</Tag>,
    },
  ];

  return (
    <div>
      <Row gutter={[10, 10]} style={{ marginBottom: 14 }}>
        <Col>
          <Select value={status} onChange={v => { setStatus(v); fetch(1, v); }} style={{ width: 130 }}>
            <Option value="pending">Pending</Option>
            <Option value="approved">Approved</Option>
            <Option value="rejected">Rejected</Option>
          </Select>
        </Col>
        <Col><Button icon={<ReloadOutlined />} onClick={() => fetch(page)} /></Col>
      </Row>
      <Table dataSource={data} columns={columns} rowKey="id" loading={loading} size="small" scroll={{ x: 800 }}
        pagination={{ current: page, pageSize: 30, total, onChange: p => { setPage(p); fetch(p); }, showTotal: t => `Total ${t}` }} />
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function SamajPropertiesList() {
  const [stats, setStats] = useState(null);
  const fetchStats = useCallback(async () => {
    try {
      const r = await axios.get(`${baseUrl}/admin/samaj-properties/stats`, { headers: auth() });
      if (r.data.status) setStats(r.data.result);
    } catch {}
  }, []);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  return (
    <div style={{ padding: '12px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 900, color: '#7a0000' }}>
            <HomeOutlined style={{ marginRight: 10 }} />
            समाज भवन / गुरुकुल / छात्रावास
          </h2>
          <div style={{ color: '#6b7280', fontSize: 13, marginTop: 4 }}>
            Community buildings, Gurukuls &amp; Chhatrawas — with Samiti, GPS &amp; user comments
          </div>
        </div>
        <Button icon={<ReloadOutlined />} onClick={fetchStats}>Refresh Stats</Button>
      </div>

      {stats && (
        <Row gutter={[10, 10]} style={{ marginBottom: 20 }}>
          {[
            { label: 'समाज भवन',     value: stats.samaj_bhawan   || 0, color: '#7a0000' },
            { label: 'छात्रावास',    value: stats.chhatrawas      || 0, color: '#0891b2' },
            { label: 'गुरुकुल',      value: stats.gurukul         || 0, color: '#15803d' },
            { label: 'Rajasthan',    value: stats.rajasthan        || 0, color: '#7c3aed' },
            { label: 'Other States', value: stats.other_state      || 0, color: '#d97706' },
            { label: 'Abroad',       value: stats.abroad           || 0, color: '#dc2626' },
            { label: 'Verified ✓',   value: stats.verified         || 0, color: '#16a34a' },
            { label: 'Pending Comments', value: stats.pending_comments || 0, color: '#dc2626' },
          ].map(s => (
            <Col key={s.label} xs={12} sm={8} md={3}>
              <Card size="small" style={{ borderLeft: `4px solid ${s.color}`, borderRadius: 10 }} bodyStyle={{ padding: '10px 14px' }}>
                <Statistic value={s.value} valueStyle={{ fontSize: 20, fontWeight: 900, color: s.color }} />
                <div style={{ fontSize: 10, color: '#6b7280' }}>{s.label}</div>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Card bodyStyle={{ padding: 0 }}>
        <Tabs defaultActiveKey="samaj_bhawan" tabBarStyle={{ paddingLeft: 16, paddingRight: 16 }}>
          <TabPane tab={<span><HomeOutlined />समाज भवन</span>} key="samaj_bhawan">
            <div style={{ padding: 16 }}><PropertiesTab typeKey="samaj_bhawan" /></div>
          </TabPane>
          <TabPane tab={<span><BankOutlined />छात्रावास</span>} key="chhatrawas">
            <div style={{ padding: 16 }}><PropertiesTab typeKey="chhatrawas" /></div>
          </TabPane>
          <TabPane tab={<span><BookOutlined />गुरुकुल</span>} key="gurukul">
            <div style={{ padding: 16 }}><PropertiesTab typeKey="gurukul" /></div>
          </TabPane>
          <TabPane tab={
            <span>
              <CommentOutlined />Comments
              {stats?.pending_comments > 0 && (
                <Badge count={stats.pending_comments} size="small" style={{ marginLeft: 6 }} />
              )}
            </span>
          } key="comments">
            <div style={{ padding: 16 }}><CommentsTab /></div>
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
}
